import React from 'react';
import { Modal } from '../modals';
import { Button } from '../ui';
import { Car } from '../../types';

interface DuplicateChassisModalProps {
    isOpen: boolean;
    onClose: () => void;
    onKeepNew: () => void;
    onCancel: () => void;
    onChooseExisting: (car: Car) => void;
    chassisNumber: string;
    existingCars: Car[];
}

export const DuplicateChassisModal: React.FC<DuplicateChassisModalProps> = ({
    isOpen,
    onClose,
    onKeepNew,
    onCancel,
    onChooseExisting,
    chassisNumber,
    existingCars
}) => {
    const existingCar = existingCars[0]; // Show first match

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={
                <div className="flex items-center gap-2">
                    <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>Chassis Already Exists</span>
                </div>
            }
            size="md"
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        variant="outline"
                        onClick={onCancel}
                    >
                        Cancel Entry
                    </Button>
                    {existingCar && (
                        <button
                            onClick={() => onChooseExisting(existingCar)}
                            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md font-medium transition-colors"
                        >
                            Choose Existing
                        </button>
                    )}
                    <Button
                        onClick={onKeepNew}
                    >
                        Keep New Unit
                    </Button>
                </div>
            }
        >
            <div className="space-y-4">
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                    <p className="text-amber-800 dark:text-amber-200 font-medium">
                        Chassis number <span className="font-mono bg-amber-100 dark:bg-amber-800 px-2 py-0.5 rounded">{chassisNumber}</span> already exists in the system.
                    </p>
                    {existingCars.length > 1 && (
                        <p className="text-amber-700 dark:text-amber-300 text-sm mt-1">
                            Found {existingCars.length} existing records with this chassis number.
                        </p>
                    )}
                </div>

                {existingCar && (
                    <div className="bg-muted/50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm uppercase tracking-wide">
                            Existing Entry Details
                        </h4>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                                <span className="text-gray-500 dark:text-gray-400">Chassis Number:</span>
                                <p className="font-mono font-medium text-gray-900 dark:text-gray-100">{existingCar.chassis_number}</p>
                            </div>

                            <div>
                                <span className="text-gray-500 dark:text-gray-400">Reference #:</span>
                                <p className="font-medium text-gray-900 dark:text-gray-100">{existingCar.reference_number}</p>
                            </div>

                            <div>
                                <span className="text-gray-500 dark:text-gray-400">Vehicle:</span>
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                    {existingCar.make_name || existingCar.make?.name || existingCar.make_code || ''}{' '}
                                    {existingCar.model_name || existingCar.model?.name || existingCar.model_code || ''}
                                </p>
                            </div>

                            <div>
                                <span className="text-gray-500 dark:text-gray-400">Voyage:</span>
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                    {existingCar.voyage_code || existingCar.voyage?.voyage_code || '-'}
                                </p>
                            </div>

                            <div>
                                <span className="text-gray-500 dark:text-gray-400">Destination:</span>
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                    {existingCar.port_code || existingCar.port?.code || '-'}
                                    {(existingCar.port?.port) && ` (${existingCar.port.port})`}
                                </p>
                            </div>

                            <div>
                                <span className="text-gray-500 dark:text-gray-400">Shipper:</span>
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                    {existingCar.shipper_code || existingCar.shipper?.code || '-'}
                                    {(existingCar.shipper?.shortname || existingCar.shipper_name || existingCar.shipper?.name) && ` (${existingCar.shipper?.shortname || existingCar.shipper_name || existingCar.shipper?.name})`}
                                </p>
                            </div>

                            <div className="col-span-2">
                                <span className="text-gray-500 dark:text-gray-400">Consignee:</span>
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                    {existingCar.name || existingCar.consignee_name || existingCar.consignee?.name || '-'}
                                </p>
                            </div>

                            <div>
                                <span className="text-gray-500 dark:text-gray-400">Date of Input:</span>
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                    {existingCar.date_of_input ? new Date(existingCar.date_of_input).toLocaleDateString() : '-'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};
